import axiosObject from './axios';
import { makeRequest, baseURL } from './https';
import { parseContentDispositionFilename } from '../utils/download.utils';
import { 
  AddChatMessageRequest, 
  VoiceMessageResponse,
  ChatbotWelcomeResponse,
  ChatbotNavigateRequest,
  ChatbotNavigateResponse,
  UpdateLastReadRequest,
  ReadDetailsResponse,
  ThreadRepliesResponse,
  EditChatMessageRequest,
  EditChatMessageResponse,
  OrgMember,
  ChatSearchResponse,
  ChatMessageListResponse,
  PinChatMessageRequest,
  PinChatMessageResponse,
} from '../types/chat.type';
import {
  ResponseTimeQueryParams,
  ResponseTimeResponse,
  ResponseTimeSummary,
} from '../types/responseTime.type';

const EMPTY_RESPONSE_TIME_SUMMARY: ResponseTimeSummary = {
  avg_hours: null,
  pct_within_24h: null,
  closed_count: 0,
  open_count: 0,
  breached_count: 0,
  target_hours: 24,
};

function unwrapResponseTimePayload(raw: unknown): ResponseTimeResponse {
  const payload = raw as Record<string, unknown>;
  if (payload.summary && Array.isArray(payload.items)) {
    return {
      summary: payload.summary as ResponseTimeSummary,
      items: payload.items as ResponseTimeResponse['items'],
      total_count: Number(payload.total_count ?? payload.total ?? payload.items.length) || 0,
    };
  }

  const nested = (payload.data ?? payload.result) as ResponseTimeResponse | undefined;
  if (nested?.summary) {
    return {
      summary: nested.summary,
      items: Array.isArray(nested.items) ? nested.items : [],
      total_count: Number(nested.total_count ?? nested.items?.length ?? 0) || 0,
    };
  }

  return {
    summary: EMPTY_RESPONSE_TIME_SUMMARY,
    items: [],
    total_count: 0,
  };
}

export const addChatMessage = (data: AddChatMessageRequest): Promise<VoiceMessageResponse> => {
  return makeRequest({
    url: '/chat',
    method: 'post',
    data,
  });
};

export const getChatMessages = (
  organization_id: string,
  limit: number,
  before_id?: string | null,
  after_id?: string | null,
  month?: string | null,  // "YYYY-MM" e.g. "2025-03"
) => {
  // When month is provided, backend uses its own internal limit — don't send limit to avoid 422
  const params: any = month ? { month } : { limit };
  if (!month && before_id) params.before_message_id = before_id;
  if (!month && after_id) params.after_message_id = after_id;
  return makeRequest({
    url: `/chat/${organization_id}`,
    method: 'get',
    params,
  });
};

// ===== Search Services =====

// Full-history search across the organization's chat messages.
export const searchChatMessages = (
  organization_id: string,
  query: string,
  limit = 50,
): Promise<ChatSearchResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/search`,
    method: 'get',
    params: { q: query, limit },
  });
};

// Load a window of messages around a specific message (used to jump to a search result).
export const getMessageContext = (
  organization_id: string,
  message_id: string,
  before = 20,
  after = 20,
): Promise<ChatMessageListResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/context/${message_id}`,
    method: 'get',
    params: { before, after },
  });
};

// ===== Chatbot Services =====

export const getChatbotWelcome = (organization_id: string): Promise<ChatbotWelcomeResponse> => {
  return makeRequest({
    url: `/chat/chatbot/welcome/${organization_id}`,
    method: 'get',
  });
};

export const navigateChatbot = (data: ChatbotNavigateRequest): Promise<ChatbotNavigateResponse> => {
  return makeRequest({
    url: '/chat/chatbot/navigate',
    method: 'post',
    data,
  });
};

// ===== Read Receipts Services =====

export const updateLastRead = (data: UpdateLastReadRequest): Promise<void> => {
  return makeRequest({
    url: '/chat/update-last-read',
    method: 'post',
    data,
  });
};

export const getReadDetails = (organization_id: string, message_id: string): Promise<ReadDetailsResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/read-details`,
    method: 'get',
    params: { message_id },
  });
};

// ===== Thread Services =====

export const getThreadReplies = (organization_id: string, parent_message_id: string, limit = 100): Promise<ThreadRepliesResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/thread/${parent_message_id}`,
    method: 'get',
    params: { limit },
  });
};

// ===== Edit Message Services =====

export const editChatMessage = (
  organization_id: string,
  message_id: string,
  data: EditChatMessageRequest,
): Promise<EditChatMessageResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/message/${message_id}`,
    method: 'patch',
    data,
  });
};

export const pinChatMessage = (
  organization_id: string,
  message_id: string,
  data: PinChatMessageRequest,
): Promise<PinChatMessageResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/message/${message_id}/pin`,
    method: 'patch',
    data,
  });
};

export const getPinnedChatMessages = (
  organization_id: string,
): Promise<ChatMessageListResponse> => {
  return makeRequest({
    url: `/chat/${organization_id}/pinned`,
    method: 'get',
  });
};

// ===== Mention Services =====

export const getOrgMembers = (organization_id: string): Promise<OrgMember[]> => {
  return makeRequest({
    url: `/chat/${organization_id}/members`,
    method: 'get',
  });
};

// ===== Voice Playback Services =====

export const getVoicePlaybackUrl = (organization_id: string, message_id: string): Promise<{ playback_url: string; expires_in: number }> => {
  return makeRequest({
    url: `/chat/${organization_id}/message/${message_id}/playback-url`,
    method: 'get',
  });
};

// ===== Asset Playback Services =====

export const getAssetPlaybackUrl = (organization_id: string, asset_id: string): Promise<{ url: string; expires_in: number }> => {
  return makeRequest({
    url: `/chat/${organization_id}/asset/${asset_id}/url`,
    method: 'get',
  });
};

// ===== Export Services =====

export interface ChatExportParams {
  format: 'txt' | 'pdf';
  from_date?: string;
  to_date?: string;
  include_threads?: boolean;
}

export const exportChatMessages = async (
  organization_id: string,
  params: ChatExportParams,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await axiosObject({
    url: `${baseURL}/chat/${organization_id}/export`,
    method: 'get',
    params,
    responseType: 'blob',
  });

  const filename =
    parseContentDispositionFilename(response.headers['content-disposition'])
    || `chat_export.${params.format}`;

  return { blob: response.data, filename };
};

export const getChatResponseTime = async (
  params?: ResponseTimeQueryParams,
): Promise<ResponseTimeResponse> => {
  const raw = await makeRequest({
    url: '/chat/response-time',
    method: 'get',
    params,
  });
  return unwrapResponseTimePayload(raw);
};